import type { TaskState } from '../services/api/projectStates';
import type { ProjectTemplateState } from '../services/api/projectTemplates';

/**
 * Three-bucket diff for the Re-apply Template modal (Phase 20 D-16, D-17).
 *
 * - `added`: states present in the template but missing from the project (matched by name).
 * - `removed`: states present in the project but missing from the template.
 * - `kept`: states present in BOTH (matched by name; color/ordering may differ but the row survives).
 *
 * Match key: case-sensitive exact `name` equality. Phase 17 D-12 dual-write
 * keeps `task.status` aligned to `task_states.name` so canonical-form names
 * are used both server-side and client-side; case-folding is unnecessary
 * for the Re-apply UX.
 *
 * Computed client-side (D-17). The backend re-apply endpoint
 * (`POST /api/admin/projects/:projectId/reapply-template`) performs its own
 * normalized check (Phase 18 D-01) and returns 409 STATE_IN_USE if the
 * removal set has tasks — that is the authoritative result. This helper is
 * a UX preview only.
 */
export interface ReapplyDiff {
  added: ProjectTemplateState[];
  removed: TaskState[];
  kept: TaskState[];
}

export function computeReapplyDiff(
  projectStates: TaskState[],
  templateStates: ProjectTemplateState[]
): ReapplyDiff {
  const projectNames = new Set(projectStates.map((s) => s.name));
  const templateNames = new Set(templateStates.map((s) => s.name));

  const added = templateStates.filter((t) => !projectNames.has(t.name));
  const removed = projectStates.filter((p) => !templateNames.has(p.name));
  const kept = projectStates.filter((p) => templateNames.has(p.name));

  return { added, removed, kept };
}
